// Single source of truth for /tools/dpa-hidden-strings.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/dpa-hidden-strings";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "What Hidden Strings Come Attached to 'Free' Down Payment Money?";
export const PAGE_DESCRIPTION =
  "Down payment assistance is rarely a no-strings gift. Here are the six tradeoffs to check first: rate premiums, silent second liens, refinance subordination, recapture tax, occupancy limits, and seller hesitation.";

export const HERO_PROMISE =
  "Down payment assistance is real money and it helps a lot of buyers get in the door years earlier. But \"free\" almost always comes with conditions written into a lien, a note, or a program agreement. This guide names the six strings that show up most often, in plain language, so you can price them before you commit.";

export const SECTIONS = [
  { id: "compare", title: "No-DPA vs DPA over 5 and 10 years" },
  { id: "risks", title: "The six strings to check" },
  { id: "scenarios", title: "When DPA helps and when it backfires" },
  { id: "checklist", title: "Trust checklist before you sign" },
  { id: "faq", title: "Frequently asked questions" },
];

export interface ComparisonRow {
  id: string;
  factor: string;
  noDpa: string;
  dpa5: string;
  dpa10: string;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    id: "rate",
    factor: "Mortgage rate",
    noDpa: "Best rate you qualify for on the open market.",
    dpa5: "Often a modest premium — commonly cited in the 0.125%–0.75% range, but it varies by agency, lender, and funding round.",
    dpa10: "Same premium, compounding over more years of interest unless you refinance out.",
  },
  {
    id: "monthly",
    factor: "Monthly cost",
    noDpa: "Higher early on if a smaller down payment means more mortgage insurance.",
    dpa5: "Usually lower up front — assistance reduces cash needed and can shrink the first-lien balance.",
    dpa10: "The rate premium can quietly erase some of the early savings over a decade.",
  },
  {
    id: "trigger",
    factor: "Repayment trigger",
    noDpa: "None. You owe only your mortgage.",
    dpa5: "Many programs call the assistance due on sale, refinance, transfer of title, or if the home stops being your primary residence.",
    dpa10: "Same triggers, but you are often past the forgiveness window by then in programs that forgive.",
  },
  {
    id: "forgiveness",
    factor: "Forgiveness period",
    noDpa: "Not applicable.",
    dpa5: "Common structures forgive over 5, 10, or 15 years — some prorate monthly, some are all-or-nothing at the end.",
    dpa10: "Longer-tenure owners typically reach full forgiveness in programs that offer it.",
  },
  {
    id: "refi",
    factor: "Refinance flexibility",
    noDpa: "Refinance whenever the math works.",
    dpa5: "You usually need the agency to subordinate its lien. Approval is not automatic, and cash-out is often restricted or denied.",
    dpa10: "Easier once forgiven or paid off, but the lien must still be cleared or resubordinated in writing.",
  },
  {
    id: "lender",
    factor: "Lender choice",
    noDpa: "Any lender you want.",
    dpa5: "Limited to lenders approved by the program's housing agency.",
    dpa10: "Same limit while the assistance lien remains on title.",
  },
];

export interface Risk {
  id: string;
  heading: string;
  summary: string;
  detail: string;
  example: string;
}

export const RISKS: Risk[] = [
  {
    id: "higher-rates",
    heading: "1. The mortgage rate is often a little higher",
    summary: "Assistance is frequently bundled with a specific first mortgage product, and that product may not carry the market's best rate.",
    detail:
      "Housing agencies fund assistance partly through the pricing of the attached first mortgage. That can mean a rate premium versus what a strong-credit borrower might get shopping the open market. The size of the premium is program-dependent and changes between funding rounds — some programs price at or near market, others clearly above it.",
    example:
      "On a $300,000 loan, a 0.5% rate premium is roughly $90–$100 more per month. If the assistance is $10,000, you are still ahead in the early years — but by year ten the premium may have consumed much of the benefit if you never refinance.",
  },
  {
    id: "silent-second",
    heading: "2. \"Forgivable\" usually means a silent second lien",
    summary: "Most assistance is recorded as a second mortgage against your home, even when no payment is ever due.",
    detail:
      "A silent second requires no monthly payment, but it is a real lien. It appears on title, it must be satisfied or subordinated for most future transactions, and it can be called due if you break a program condition. Some programs use a deferred repayable loan instead, which is owed in full regardless of how long you stay. Read which one you are signing.",
    example:
      "A buyer who thinks they received a grant discovers at closing on a sale five years later that $12,000 has to come out of the proceeds because the forgiveness clock had not finished.",
  },
  {
    id: "subordination",
    heading: "3. Refinancing needs the agency's permission",
    summary: "The assistance lien has to be resubordinated behind your new first mortgage, and that is a separate approval.",
    detail:
      "When rates drop, you cannot simply refinance. The program administrator must agree to stay in second position. Policies vary widely: some subordinate routinely for rate-and-term refinances, some charge a fee, some take weeks, and many refuse cash-out refinances outright. A refusal can force you to pay off the assistance to refinance at all.",
    example:
      "Rates fall a full point. Your refinance saves $200 a month, but the agency will only subordinate for a rate-and-term refinance, so the $15,000 renovation cash-out you planned is off the table.",
  },
  {
    id: "recapture",
    heading: "4. Recapture tax can apply to bond-funded programs",
    summary: "Some federally-backed mortgage revenue bond programs can claw back a portion of your gain if you sell early.",
    detail:
      "Recapture tax is tied to certain bond-funded first mortgages, not to every DPA program. Where it applies, the exposure commonly phases out over roughly a nine-year window and only bites if you sell at a gain and your income has risen above program thresholds. Many buyers who trigger the rules owe nothing. Some agencies reimburse borrowers who do owe. Ask specifically whether your loan is subject to recapture and get the answer in writing.",
    example:
      "You sell in year four with a solid gain and a much higher income than at purchase. The recapture calculation applies — but because the capped amount is a share of gain and of the original loan, the actual bill may be far smaller than the worst case, or zero.",
  },
  {
    id: "occupancy",
    heading: "5. You have to actually live there",
    summary: "Nearly every program requires the home to remain your primary residence, and renting it out can be a default.",
    detail:
      "Occupancy is the condition programs enforce most consistently. Moving out, converting the property to a rental, or listing it short-term can trigger repayment of the full assistance amount, sometimes with interest. A few programs allow temporary hardship or military exceptions, but you generally have to request them before you move.",
    example:
      "A job transfer in year three tempts you to rent the house out instead of selling. That likely converts a forgivable second into a bill due now — check the program agreement before you sign a lease.",
  },
  {
    id: "seller-hesitation",
    heading: "6. Sellers sometimes hesitate on DPA offers",
    summary: "In competitive markets, an assistance-backed offer can be seen as slower or riskier than a conventional one.",
    detail:
      "Some assistance programs add layers: agency underwriting, an extra approval step, occasional property condition standards, and slightly longer timelines. Listing agents who have been burned before may steer a seller toward a cleaner offer. This is perception as much as reality — a well-prepared file with a program-experienced lender often closes on a normal timeline — but it is worth planning for.",
    example:
      "Two offers at the same price: yours with agency assistance, theirs conventional. Countering with a firm closing date and a lender letter that names the program's typical turn time is what usually levels the field.",
  },
];

export interface Scenario {
  id: string;
  title: string;
  verdict: "helps" | "depends" | "backfires";
  verdictLabel: string;
  body: string;
}

export const SCENARIOS: Scenario[] = [
  {
    id: "long-term",
    title: "You plan to stay 10+ years",
    verdict: "helps",
    verdictLabel: "Usually helps",
    body:
      "You are the buyer these programs are built for. You reach the end of most forgiveness schedules, you clear recapture windows, and the assistance functions close to a true grant. The main cost to watch is the rate premium over a long horizon — run the numbers on refinancing once forgiveness completes.",
  },
  {
    id: "refinancer",
    title: "You expect to refinance when rates drop",
    verdict: "depends",
    verdictLabel: "Depends on the program",
    body:
      "Everything hinges on the agency's subordination policy. Before you commit, ask for it in writing: do they subordinate for rate-and-term refinances, what does it cost, how long does it take, and do they allow cash-out. A program that subordinates routinely is fine. One that does not can lock you into today's rate or force an early payoff.",
  },
  {
    id: "may-move",
    title: "You may move or rent the home within 5 years",
    verdict: "backfires",
    verdictLabel: "Often backfires",
    body:
      "Short-tenure buyers hit repayment triggers before forgiveness accrues, may face recapture exposure on bond-funded loans, and risk an occupancy default if they rent instead of selling. If a move is likely, price the assistance as a loan you will repay, not as free money — and compare it against a conventional low-down-payment loan.",
  },
];

export interface ChecklistItem {
  id: string;
  label: string;
  question: string;
}

export const CHECKLIST: ChecklistItem[] = [
  { id: "lender", label: "Lender lock-in", question: "Which lenders are approved for this program, and can I still shop among them for the best pricing?" },
  { id: "premium", label: "Rate premium", question: "What is the rate on the program's first mortgage versus the best rate I qualify for without it — in writing, same day?" },
  { id: "lien", label: "Lien type", question: "Is the assistance a grant, a forgivable second, or a deferred repayable second? Will it be recorded against title?" },
  { id: "forgiveness", label: "Forgiveness schedule", question: "If it is forgivable, does it prorate monthly or forgive all at once, and on exactly what date does it finish?" },
  { id: "subordination", label: "Subordination policy", question: "Will you subordinate for a future refinance? Rate-and-term only, or cash-out too? What is the fee and timeline?" },
  { id: "resale", label: "Resale triggers", question: "What exactly makes the money due — sale, refinance, transfer of title, death, shared appreciation?" },
  { id: "occupancy", label: "Occupancy default", question: "What happens if I have to move, rent the home, or deploy? Are hardship exceptions available and how do I request one?" },
];

export interface Faq {
  id: string;
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  {
    id: "really-free",
    q: "Is down payment assistance really free?",
    a: "Sometimes, but not usually. A true grant with no lien and no repayment condition exists in some local programs. Far more common is a forgivable second mortgage that becomes free only after you meet a residency term, or a deferred loan that is always repayable. Ask which of the three you are being offered before you assume it is a gift.",
  },
  {
    id: "sell-or-refi-early",
    q: "What happens if I refinance or sell early?",
    a: "In most programs, selling triggers repayment of whatever portion has not been forgiven, paid from your sale proceeds at closing. Refinancing generally requires the program to subordinate its lien behind the new loan; if they decline, you would have to pay the assistance off to complete the refinance. Both outcomes are program-dependent, so get the specific terms from the administrator.",
  },
  {
    id: "credit-score",
    q: "Does DPA hurt my credit or my approval odds?",
    a: "The assistance itself does not damage your credit. It can affect approval indirectly, because you must qualify under both the lender's guidelines and the program's overlays, which may add income caps, homebuyer education requirements, and stricter debt-to-income limits.",
  },
  {
    id: "rate-premium-size",
    q: "How much higher is the interest rate with assistance?",
    a: "It varies. Some agency products price close to market; others carry a noticeable premium. The only reliable answer is a same-day comparison: ask your lender to quote the program loan and a standard loan side by side, with all fees, on the same date.",
  },
  {
    id: "recapture-who",
    q: "Who actually owes recapture tax?",
    a: "Recapture applies to certain mortgage revenue bond-funded loans, not to all assistance. Where it applies, you generally owe only if you sell within the phase-out window, sell at a gain, and your income has risen above program limits. Many affected sellers owe nothing, and some agencies reimburse those who do. Confirm with a tax professional and your program administrator.",
  },
  {
    id: "rent-it-out",
    q: "Can I rent out a home bought with down payment assistance?",
    a: "Usually not while the assistance lien is active. Nearly all programs require continuous primary occupancy, and converting to a rental is commonly treated as a default that makes the full amount due. Some programs allow written hardship or military exceptions if you request them in advance.",
  },
  {
    id: "worth-it",
    q: "So is it still worth taking?",
    a: "For buyers who plan to stay put, very often yes — a few thousand dollars of extra interest is a small price for buying years earlier. For buyers who may move, refinance soon, or rent the property, treat the assistance as a loan and compare it honestly against a conventional low-down-payment option.",
  },
];

export const COMPLIANCE_NOTE =
  "Program terms differ by state housing agency, local administrator, lender, and funding round, and they change between rounds. Rate premiums, forgiveness schedules, recapture exposure, subordination policies, and default triggers described here are typical patterns, not guarantees about any specific program. Nothing on this page is a determination of eligibility or a commitment to lend.";

export const DISCLAIMER =
  "Educational information only, not legal, tax, or lending advice. Home Approach is not a lender and does not administer assistance programs. Confirm every term in writing with the program administrator and your lender, and consult a tax professional about recapture.";

export const DPA_HIDDEN_STRINGS_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
